import { Injectable } from "@angular/core";
import { TeamRemoteControllerService } from "core/api";
import { ConfigService } from "core/config";
import { EProcessSharing } from "core/dto";
import { DynamicFormService, IControl, IDateMinRule } from "core/dynamic-form";
import { filterAllTruthyInTuple } from "core/rx-js";
import { dateUtils } from "core/utils";
import { UserService } from "projects/designer/src/app/global-services/user.service";
import { Subject, combineLatest, debounceTime, distinct, distinctUntilChanged, filter, map, skipWhile, switchMap, take, takeUntil } from "rxjs";
import { ProcessStepperService } from "../../../process-stepper.service";
import { RemoteProcessService } from "../../../remotes/remote-process.service";
import { SignProcessWizardService } from "../../../sign-process-wizard.service";
import { DocumentUploadStepService } from "../document-upload-step.service";



@Injectable()
export class DocumentInfoFormService {

  private readonly _destroy = new Subject<void>();

  readonly mainFormGroupSubject = this._dynamicFormService.mainFormGroupSubject;
  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  readonly stepChangedSubject = this._processStepperService.stepChangedSubject;

  readonly leftDocumentUploadStepObs = this.stepChangedSubject.pipe(
    map(({ from }) => from === 'documentUpload'),
    distinctUntilChanged(),
    filter(Boolean),
  )

  readonly processSubject = this._signProcessWizardService.processSubject;
  readonly processInfoSubject = this._signProcessWizardService.processInfoSubject;
  get #processInfo() {
    return this.processInfoSubject.value;
  }



  get #defaultProcessSharing() {
    if (this._configService.appConfig.processSharingDefault === 'creatorSigners') {
      return EProcessSharing.creatorSigners
    }

    return EProcessSharing.creatorSignersTeams
  }



  constructor(
    private readonly _dynamicFormService: DynamicFormService,
    private readonly _documentUploadStepService: DocumentUploadStepService,
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _processStepperService: ProcessStepperService,
    private readonly _remoteProcessService: RemoteProcessService,
    private readonly _userService: UserService,
    private readonly _teamRemoteControllerService: TeamRemoteControllerService,
    private readonly _configService: ConfigService
  ) {


    const defaultValidTill = dateUtils.add('day', new Date(), this._configService.appConfig.processExpirationInDaysDefault)
    this._dynamicFormService.controls = [
      {
        dataField: 'description',
        label: 'ss-entities.process.title',
        type: 'text',
        validationRules: [{ type: 'required' }]
      },
      {
        dataField: 'validTill',
        label: 'ss-entities.process.valid-until',
        type: 'date',
        options: { min: new Date() },

        validationRules: [
          { min: new Date(), type: 'dateMin' } as IDateMinRule
        ],

        defaultValue: this.processSubject.value?.validTill || defaultValidTill
      } as IControl<'date'>,
      {
        dataField: 'processSharingType',
        label: 'ss-entities.process.process-sharing',
        type: 'select',
        options: {
          listItems: [
            { text: 'ss-entities.enum-process-sharing.creator-signers', value: EProcessSharing.creatorSigners },
            { text: 'ss-entities.enum-process-sharing.creator-signers-teams', value: EProcessSharing.creatorSignersTeams }
          ],
          displayExpr: 'text',
          valueExpr: 'value'
        },
        defaultValue: this.#defaultProcessSharing,
        validationRules: [{ type: 'required' }]
      },
      {
        dataField: 'note',
        label: 'ss-entities.process.note',
        type: 'textarea',
        colSpan: 2,
        defaultValue: null
      }
    ]

    this.mainFormGroupSubject
      .pipe(
        filter(Boolean),
        switchMap((formGroup) => formGroup.valueChanges),
        debounceTime(100),
        takeUntil(this._destroy)
      )
      .subscribe((value) => {

        this.processInfoSubject.next({
          ...this.#processInfo,
          ...value
        })
      })

    this._documentUploadStepService.triedNextObs
      .pipe(
        takeUntil(this._destroy),
        distinct()
      )
      .subscribe(triedNext => this._dynamicFormService.submittedSubject.next(Boolean(triedNext)))




    combineLatest([this.processInfoSubject, this.processSubject])
      .pipe(
        filterAllTruthyInTuple(),
        debounceTime(200),
        filter(([formData, process]) => {

          const dataIsDifferent = formData.description !== process.description
            || Math.abs(formData.validTill?.getTime?.() - process.validTill?.getTime?.()) > 100 * 1000
            || formData.processSharingType !== process.processSharingType
            || formData.note !== process.note
            || formData.signingOrderType !== process.signingOrderType

          return dataIsDifferent && Boolean(formData.description && formData.validTill)
        }),
        map(([formData]) => formData),
        debounceTime(200),
        distinctUntilChanged((prev, next) => {

          return JSON.stringify(prev) === JSON.stringify(next)
        })
      )
      .subscribe((formData) => {

        const { description, validTill, note, processSharingType, signingOrderType } = formData;
        this._remoteProcessService.update({ description, validTill, processSharingType, note, signingOrderType }, 'updateLocal').pipe(take(1)).subscribe();
      })


    combineLatest([this.processSubject, this.mainFormGroupSubject])
      .pipe(
        takeUntil(this._destroy),
        filterAllTruthyInTuple(),
        take(1)
      )
      .subscribe(([{ description, validTill, note, processSharingType }, mainFormGroup]) => {
        mainFormGroup.patchValue({ description, validTill, note, processSharingType })
      })


    combineLatest([this.mainFormGroupSubject, this.processIdSubject])
      .pipe(
        takeUntil(this._destroy),
        filter(([, processId]) => !processId),
        map(([mainFormGroup]) => mainFormGroup),
        switchMap((mainFormGroup) => combineLatest([
          mainFormGroup.valueChanges,
          mainFormGroup.statusChanges
        ])),
        skipWhile(([_, status]) => status !== 'VALID'),
        debounceTime(1000),
        map(([formData]) => formData),
        take(1)
      )
      .subscribe(() => {
        this._remoteProcessService.insert(this.processInfoSubject.value).pipe(take(1)).subscribe();
      })
  }


  ngOnDestroy() {
    this._destroy.next();
    this._destroy.complete();
  }
}



